import * as _ from 'underscore';
import * as $ from 'jquery';

import { WidgetData } from '../../../../_common/platform/ts/WidgetData';

import BaseStore from '../../../../_common/platform/ts/BaseStore';
import { apiCall } from '../../../../_common/platform/ts/AqlUtility';
import { Constants } from '../../../../_common/platform/ts/Constants';

import { AppDispatcher as Dispatcher } from '../dispatcher/AppDispatcher';
import { Action, AppActions, I, check as checkAction } from '../actions/AppActions';

import { IGlob } from './../Glob';

export interface ITemplateEditState {
    id: any;
    name?: string;
    data?: WidgetData;
    dirId?: number;
    loading: boolean;
    actionLoading: boolean;
}

export class TemplateEditStore extends BaseStore<ITemplateEditState, AppActions, Dispatcher, IGlob> {
    public _state: ITemplateEditState;
    public static _name = 'template-edit';

    public static getInitialState() {
        return {
            id: null,

            loading: false,
            actionLoading: false
        };
    }

    private _defs: any = {};

    private show(id) {
        this._state = TemplateEditStore.getInitialState();

        if (id != null) {
            this._state.id = id;

            this.actions.templateLoadContent(id);
        }
    }

    private hide(id) {
        this._state = TemplateEditStore.getInitialState();
    }

    private requestName(id) {
        return apiCall<string>(this.actions, 'widgetTemplate_GetName', {
            wtid: id
        });
    }

    private requestData(id) {
        return apiCall<string>(this.actions, 'widgetTemplate_GetData', {
            wtid: id
        });
    }

    private loadContent(id) {
        this._state.loading = true;

        if (this._defs.initialLoad != null && this._defs.initialLoad.state() === 'pending') {
            this._defs.initialLoad.resolve(
                {
                    result: ''
                },
                {
                    result: '{}'
                }
            );
        }

        const def = $.when(this.requestName(id), this.requestData(id));

        this._defs.initialLoad = def;

        def.done((nameResp, dataResp) => {
            this._state.name = nameResp.result;

            const res = WidgetData.getFromJSON(dataResp.result, this.glob);

            if (res.result) {
                this._state.data = res.widgetData;
            }
        });

        def.fail((resp) => {
            this.actions.showNotification_Fail(resp.error && resp.error.message);
        });

        def.always(() => {
            this.actions.templateLoadContentFinish(id);
        });
    }

    private loadContentFinish(id) {
        this._state.loading = false;
    }

    private create(dirId) {
        this._state.name = 'New widget';
        this._state.data = WidgetData.getEmpty(this.glob);
        this._state.dirId = dirId;
    }

    private templateSave(id, data) {
        this._state.actionLoading = true;

        if (this._defs.save != null && this._defs.save.state() === 'pending') {
            this._defs.save.resolve(null);
        }

        const def = $.Deferred<any>();

        this._defs.save = def;

        apiCall(
            this.actions,
            'widgetTemplate_Update',
            {
                wtid: id,
                data: data
            },
            null,
            def
        );

        def.done(() => {
            this.actions.templateSaveFinish(id, null, null, data);

            this.actions.notification('success', 'Widget "' + id + '" was successfully saved');
        });

        def.fail((obj) => {
            this.actions.templateSaveFinish();
        });

        def.always(() => {});
    }

    private templateSaveFinish() {
        this._state.actionLoading = false;
    }

    private remove(id, instanceId = 'default') {
        var def = apiCall(this.actions, 'widgetTemplate_Delete', {
            wtid: id
        });

        def.done(() => {
            this.actions.templateRemoveFinish(true, id, instanceId);

            this.actions.notification('success', 'Widget "' + id + '" was successfully deleted');
        });

        def.fail((obj) => {
            this.actions.templateRemoveFinish(false, id, instanceId);
        });
    }

    private createSave(dirId, name, overwrite, data) {
        this._state.actionLoading = true;

        if (this._defs.createSave != null && this._defs.createSave.state() === 'pending') {
            this._defs.createSave.reject(null);
        }

        const def = $.Deferred<any>();

        this._defs.createSave = def;

        apiCall(
            this.actions,
            'widgetTemplate_Create',
            {
                dirid: dirId,
                name: name,
                overwrite: overwrite,
                data: data
            },
            null,
            def
        );

        def.done((resp) => {
            var id = resp.result;

            this.actions.templateSaveFinish(id, dirId, name, data);
            this.actions.templateHide(null);

            this.actions.showNotification_Success('Widget "' + name + '" was successfully created');
        });

        def.fail((obj) => {
            this.actions.templateSaveFinish();
        });

        def.always((id) => {});
    }

    private rename(id, value) {
        this._state.actionLoading = true;

        if (this._defs.rename != null && this._defs.rename.state() === 'pending') {
            this._defs.rename.resolve(null);
        }

        const def = $.Deferred<any>();

        this._defs.rename = def;

        apiCall(
            this.actions,
            'widgetTemplate_Rename',
            {
                wtid: id,
                name: value
            },
            null,
            def
        );

        def.done(() => {
            this._state.name = value;
            this.actions.templateSaveFinish(id, null, this._state.name, null);

            this.actions.showNotification_Success('Widget "' + id + '" was successfully renamed to "' + value + '"');
        });

        def.fail((obj) => {
            this.actions.templateSaveFinish();
        });

        def.always(() => {});
    }

    private setWidgetType(id, type) {
        if (this._state.data == null) {
            return;
        }

        if (this._state.id !== id && this._state.data.id !== id && this._state.data.cid !== id) {
            return;
        }

        this._state.data.setWidgetType(type, false);
    }

    private setFieldValues(id, type, values) {
        if (this._state.data == null) {
            return;
        }

        if (this._state.id !== id && this._state.data.id !== id && this._state.data.cid !== id) {
            return;
        }

        this._state.data.setFieldValues(type, values);
    }

    private setLock(id, type, fieldName, value) {
        if (this._state.id !== id) {
            return;
        }

        this._state.data.setLock(type, fieldName, value);
    }

    private widgetModuleLoadStart(type) {
        if (this._state.data == null) {
            return;
        }
    }

    private widgetModuleLoadFinish(type) {
        if (this._state.data == null) {
            return;
        }
        if (this._state.data.type !== type) {
            return;
        }

        this._state.data = this._state.data.clone();
    }

    protected eventsHandler(payload) {
        if (!super.eventsHandler(payload)) {
            return false;
        }

        const a: Action = payload.action;
        const _case = checkAction;

        do {
            if (_case<I.TEMPLATE_SHOW>(a, Constants.TEMPLATE_SHOW)) {
                this.show(a.id);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_HIDE>(a, Constants.TEMPLATE_HIDE)) {
                this.hide(a.id);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_CONTENT_LOAD>(a, Constants.TEMPLATE_CONTENT_LOAD)) {
                this.loadContent(a.id);

                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.TEMPLATE_CONTENT_LOAD_FINISH>(a, Constants.TEMPLATE_CONTENT_LOAD_FINISH)) {
                this.loadContentFinish(a.id);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_SAVE>(a, Constants.TEMPLATE_SAVE)) {
                this.templateSave(a.id, a.data);

                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.TEMPLATE_SAVE_FINISH>(a, Constants.TEMPLATE_SAVE_FINISH)) {
                this.templateSaveFinish();

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_DELETE>(a, Constants.TEMPLATE_DELETE)) {
                this.remove(a.id, a.instanceId);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_CREATE_NEW>(a, Constants.TEMPLATE_CREATE_NEW)) {
                this.create(a.dirId);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_CREATE_SAVE>(a, Constants.TEMPLATE_CREATE_SAVE)) {
                this.createSave(a.dirId, a.name, a.overwrite, a.data);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_RENAME>(a, Constants.TEMPLATE_RENAME)) {
                this.rename(a.id, a.value);

                this.emitChange();
                break;
            }

            if (_case<I.CONFIG_SET_WIDGET_TYPE>(a, Constants.CONFIG_SET_WIDGET_TYPE)) {
                this.setWidgetType(a.id, a.type);

                this.emitChange();
                break;
            }

            if (_case<I.CONFIG_SET_FIELD_VALUES>(a, Constants.CONFIG_SET_FIELD_VALUES)) {
                this.setFieldValues(a.id, a.type, a.values);

                this.emitChange();
                break;
            }

            if (_case<I.TEMPLATE_SET_LOCK>(a, Constants.TEMPLATE_SET_LOCK)) {
                this.setLock(a.id, a.type, a.fieldName, a.value);

                this.emitChange();
                break;
            }

            if (_case<I.WIDGET_MODULE_LOAD_START>(a, Constants.WIDGET_MODULE_LOAD_START)) {
                this.widgetModuleLoadStart(a.type);

                this.emitChangeWithoutSaving();
                break;
            }

            if (_case<I.WIDGET_MODULE_LOAD_FINISH>(a, Constants.WIDGET_MODULE_LOAD_FINISH)) {
                this.widgetModuleLoadFinish(a.type);

                this.emitChange();
                break;
            }
        } while (false);

        return true;
    }

    constructor(glob, dispatcher: Dispatcher, actions: AppActions) {
        super(glob, dispatcher, actions, TemplateEditStore._name);

        this._state = _.extend(TemplateEditStore.getInitialState(), this._state);
    }

    static get(glob, dispatcher: Dispatcher, actions: AppActions) {
        return new TemplateEditStore(glob, dispatcher, actions);
    }
}
